// @df/ui - Google Analytics loader utility
// Dynamically loads Google Analytics only after user consent
const GOOGLE_TAG_BASE_URL = 'https://www.googletagmanager.com/gtag/js?id=';
const GOOGLE_TAG_SCRIPT_ATTR = 'data-df-google-analytics';
const GOOGLE_TAG_SCRIPT_STATE_ATTR = 'data-df-google-analytics-state';
const DEFAULT_CONSENT_STATE = {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
};
const ACCEPTED_CONSENT_STATE = {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
};
let analyticsLoadStatus = 'idle';
let activeMeasurementId = null;
let configuredMeasurementId = null;
let inFlightLoadPromise = null;
function getGoogleTagScriptSrc(measurementId) {
    return `${GOOGLE_TAG_BASE_URL}${measurementId}`;
}
function getManagedGoogleTagScript(measurementId) {
    return document.querySelector(`script[${GOOGLE_TAG_SCRIPT_ATTR}="${measurementId}"]`);
}
function setManagedScriptState(script, state) {
    script.setAttribute(GOOGLE_TAG_SCRIPT_STATE_ATTR, state);
}
function ensureAnalyticsQueue() {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
        window.gtag = function gtag() {
            window.dataLayer.push(arguments);
        };
    }
    return window.gtag;
}
function queueConsentAndConfiguration(measurementId) {
    if (configuredMeasurementId === measurementId) {
        return;
    }
    const gtag = ensureAnalyticsQueue();
    gtag('consent', 'default', DEFAULT_CONSENT_STATE);
    gtag('consent', 'update', ACCEPTED_CONSENT_STATE);
    gtag('js', new Date());
    gtag('config', measurementId);
    configuredMeasurementId = measurementId;
}
function resetFailedAnalyticsState(measurementId, options = {}) {
    const script = getManagedGoogleTagScript(measurementId);
    if (script?.parentNode) {
        script.parentNode.removeChild(script);
    }
    analyticsLoadStatus = 'idle';
    activeMeasurementId = null;
    configuredMeasurementId = options.preserveConfiguredMeasurement ? measurementId : null;
    inFlightLoadPromise = null;
}
/**
 * Load Google Analytics dynamically
 *
 * This function safely injects the GA script into the document and initializes
 * the gtag function. It can be called multiple times safely - it will only
 * load GA once per page session.
 *
 * @param measurementId - Google Analytics measurement ID (e.g., 'G-XXXXXXXXXX')
 *
 * @example
 * // Load GA after user accepts consent
 * if (consentStatus === 'accepted') {
 *   loadGoogleAnalytics('G-7DP8Q69SYX');
 * }
 */
export function loadGoogleAnalytics(measurementId) {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return Promise.resolve();
    }
    const existingManagedScript = getManagedGoogleTagScript(measurementId);
    if (analyticsLoadStatus === 'ready' &&
        activeMeasurementId === measurementId &&
        configuredMeasurementId === measurementId &&
        typeof window.gtag === 'function') {
        console.log('[Analytics] Google Analytics already loaded');
        return Promise.resolve();
    }
    if (existingManagedScript &&
        existingManagedScript.getAttribute(GOOGLE_TAG_SCRIPT_STATE_ATTR) === 'ready' &&
        typeof window.gtag === 'function') {
        analyticsLoadStatus = 'ready';
        activeMeasurementId = measurementId;
        configuredMeasurementId = measurementId;
        console.log('[Analytics] Google Analytics already loaded');
        return Promise.resolve();
    }
    if (analyticsLoadStatus === 'loading' &&
        activeMeasurementId === measurementId &&
        inFlightLoadPromise) {
        return inFlightLoadPromise;
    }
    if (existingManagedScript) {
        resetFailedAnalyticsState(measurementId);
    }
    console.log('[Analytics] Loading Google Analytics:', measurementId);
    const hadExistingDataLayer = Array.isArray(window.dataLayer);
    const hadExistingGtag = typeof window.gtag === 'function';
    const cleanupQueuedInitialization = (preserveConfiguredMeasurement) => {
        if (!hadExistingGtag && window.gtag) {
            delete window.gtag;
        }
        if (!hadExistingDataLayer && Array.isArray(window.dataLayer)) {
            delete window.dataLayer;
        }
        resetFailedAnalyticsState(measurementId, { preserveConfiguredMeasurement });
    };
    try {
        queueConsentAndConfiguration(measurementId);
    }
    catch (error) {
        cleanupQueuedInitialization(false);
        return Promise.reject(error instanceof Error ? error : new Error('Unknown Google Analytics initialization error'));
    }
    analyticsLoadStatus = 'loading';
    activeMeasurementId = measurementId;
    inFlightLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.async = true;
        script.src = getGoogleTagScriptSrc(measurementId);
        script.setAttribute(GOOGLE_TAG_SCRIPT_ATTR, measurementId);
        setManagedScriptState(script, 'loading');
        const cleanupFailedAttempt = (error) => {
            cleanupQueuedInitialization(hadExistingDataLayer);
            console.warn('[Analytics] Failed to load Google Analytics', error);
            reject(error);
        };
        script.addEventListener('load', () => {
            analyticsLoadStatus = 'ready';
            activeMeasurementId = measurementId;
            setManagedScriptState(script, 'ready');
            inFlightLoadPromise = null;
            console.log('[Analytics] Google Analytics loaded successfully');
            resolve();
        }, { once: true });
        script.addEventListener('error', () => {
            cleanupFailedAttempt(new Error(`Unable to load ${getGoogleTagScriptSrc(measurementId)}`));
        }, { once: true });
        document.head.appendChild(script);
    });
    return inFlightLoadPromise;
}
